import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import SearchableSelect from '../../components/SearchableSelect';
import { logError } from '../../utils/logError';
import { colors, radius, shadows } from '../../styles/theme';
import { normalizeForSearch, searchMatchPre } from '../../utils/searchMatch';
import { exportCsv } from '../../utils/analytics/export';
import {
  gruplaVadeKovalari,
  yontemBazliToplam,
  odemelerToCsvRows,
  CEK_DURUM_ETIKET,
  CEK_YONTEMLERI,
} from '../../utils/odemeTakip';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Yöntem kodu → SUNUM rengi (etiket metni API'den gelir, bu yalnız renk fallback'i).
const YONTEM_RENK = {
  CEK: { bg: '#e0f2fe', color: '#0369a1' },
  NAKIT: { bg: '#dcfce7', color: '#166534' },
  HAVALE: { bg: '#f3e8ff', color: '#7c3aed' },
  KREDI_KARTI: { bg: '#fef3c7', color: '#92400e' },
  SENET: { bg: '#fee2e2', color: '#991b1b' },
  ACIK_HESAP: { bg: '#f0f4ff', color: '#3730a3' },
  IADE: { bg: '#ecfeff', color: '#0e7490' },
  FIYAT_FARKI: { bg: '#fdf2f8', color: '#9d174d' },
};
const YONTEM_RENK_FALLBACK = { bg: '#f1f5f9', color: '#475569' };

const fmt = (v) => (v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Vade takvimi kova tanımları — sıra + başlık + renk.
const KOVA_TANIM = [
  { kod: 'GECIKMIS', baslik: 'Gecikmiş', renk: colors.danger },
  { kod: 'YEDI_GUN', baslik: '7 gün içinde', renk: colors.warning },
  { kod: 'OTUZ_GUN', baslik: '30 gün içinde', renk: colors.info },
  { kod: 'ILERIDE', baslik: 'İleride', renk: colors.textSecondary },
];

const DURUM_RENK = {
  BEKLEMEDE: { bg: '#f1f5f9', color: '#475569' },
  TAHSIL_EDILDI: { bg: colors.successLight, color: '#166534' },
  KARSILIKSIZ: { bg: colors.dangerLight, color: '#991b1b' },
};

// Yöntem/durum rozetleri için ortak görsel temel.
const badgeBaseStyle = {
  display: 'inline-block', padding: '3px 10px', borderRadius: radius.full,
  fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
};

const YontemBadge = ({ yontem, label }) => {
  const r = YONTEM_RENK[yontem] || YONTEM_RENK_FALLBACK;
  return (
    <span style={{ ...badgeBaseStyle, background: r.bg, color: r.color }}>
      {label || yontem || '-'}
    </span>
  );
};

const DurumBadge = ({ durum }) => {
  if (!durum) return <span style={{ color: colors.textTertiary }}>—</span>;
  const r = DURUM_RENK[durum] || YONTEM_RENK_FALLBACK;
  return (
    <span style={{ ...badgeBaseStyle, background: r.bg, color: r.color }}>
      {CEK_DURUM_ETIKET[durum] || durum}
    </span>
  );
};

// Vade panelindeki / tablo satırındaki aksiyon butonları.
const AksiyonButonlari = ({ odeme, onIsaretle, compact, busy }) => (
  <span style={{ display: 'inline-flex', gap: '6px', whiteSpace: 'nowrap' }}>
    <button
      onClick={(e) => { e.stopPropagation(); onIsaretle(odeme, 'TAHSIL_EDILDI'); }}
      disabled={busy}
      title="Çek/senet tahsil edildi olarak işaretle"
      style={{
        background: colors.successLight, color: '#166534', border: 'none',
        borderRadius: radius.sm, padding: compact ? '4px 8px' : '5px 10px',
        cursor: busy ? 'default' : 'pointer', fontSize: '11px', fontWeight: 600,
        opacity: busy ? 0.5 : 1,
      }}>
      Tahsil edildi
    </button>
    <button
      onClick={(e) => { e.stopPropagation(); onIsaretle(odeme, 'KARSILIKSIZ'); }}
      disabled={busy}
      title="Çek/senet karşılıksız olarak işaretle"
      style={{
        background: colors.dangerLight, color: '#991b1b', border: 'none',
        borderRadius: radius.sm, padding: compact ? '4px 8px' : '5px 10px',
        cursor: busy ? 'default' : 'pointer', fontSize: '11px', fontWeight: 600,
        opacity: busy ? 0.5 : 1,
      }}>
      Karşılıksız
    </button>
  </span>
);

const thStyle = (align = 'left') => ({
  padding: '12px 16px', textAlign: align, fontSize: '11px', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textTertiary,
  whiteSpace: 'nowrap',
});

const OdemeTakibiPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const canManage = ['muhasebe', 'yonetim', 'admin'].includes(user?.role);

  const [odemeler, setOdemeler] = useState([]);
  const [tedarikciler, setTedarikciler] = useState({});
  const [yontemler, setYontemler] = useState([]);
  const [firmalar, setFirmalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patchInFlight, setPatchInFlight] = useState(() => new Set());

  // Liste/filtre state'i URL'de (geri=korunur, link paylaşılabilir).
  const [arama, setArama] = useUrlState('arama', '', { debounceMs: 300 });
  const [yontem, setYontem] = useUrlState('yontem', '');
  const [firma, setFirma] = useUrlState('firma', '');
  const [baslangic, setBaslangic] = useUrlState('baslangic', '');
  const [bitis, setBitis] = useUrlState('bitis', '');
  const [gorunum, setGorunum] = useUrlState('gorunum', 'duz');
  // Geçersiz ?gorunum=xyz değeri düz listeye iner (doğru buton vurgulu kalır).
  const gorunumNorm = gorunum === 'grup' ? 'grup' : 'duz';

  // Ödemeleri çek — backend tarih/firma/yöntem filtrelerini uygular (sunucu tarafı).
  const fetchOdemeler = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (baslangic) params.append('baslangic', baslangic);
      if (bitis) params.append('bitis', bitis);
      if (firma) params.append('firma', firma);
      if (yontem) params.append('yontem', yontem);
      const qs = params.toString();
      const res = await fetch(`${API_URL}/api/odemeler${qs ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setOdemeler(Array.isArray(data) ? data : []);
    } catch (e) {
      logError(e);
      toast.error('Ödemeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [baslangic, bitis, firma, yontem, toast]);

  // Tedarikçi / yöntem / firma master verisi bir kez yüklenir.
  useEffect(() => {
    const fetchMaster = async () => {
      try {
        const [tRes, yRes, fRes] = await Promise.all([
          fetch(`${API_URL}/api/tedarikciler`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/odeme-yontemleri`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/firmalar`, { headers: getAuthHeaders() }),
        ]);
        if (tRes.ok) {
          const data = await tRes.json();
          const map = {};
          (Array.isArray(data) ? data : []).forEach(t => { map[t.id] = t.ad || t.unvan || `#${t.id}`; });
          setTedarikciler(map);
        }
        if (yRes.ok) {
          const data = await yRes.json();
          setYontemler(Array.isArray(data) ? data : []);
        }
        if (fRes.ok) {
          const data = await fRes.json();
          setFirmalar(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        logError(e);
        toast.error('Filtre verileri yüklenemedi');
      }
    };
    fetchMaster();
  }, [toast]);

  // Filtre değişince yalnız ödemeleri yeniden çek (sessiz refetch).
  useEffect(() => { fetchOdemeler(); }, [fetchOdemeler]);

  // cek_durum güncelleme — optimistic + toast + rollback + tek-uçuş guard.
  const isaretle = async (odeme, durum) => {
    if (patchInFlight.has(odeme.id)) return;
    setPatchInFlight(prev => { const n = new Set(prev); n.add(odeme.id); return n; });
    const onceki = odeme.cek_durum;
    setOdemeler(prev => prev.map(o => o.id === odeme.id ? { ...o, cek_durum: durum } : o));
    try {
      const res = await fetch(`${API_URL}/api/odemeler/${odeme.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cek_durum: durum }),
      });
      if (!res.ok) throw new Error(await res.text());
      const tur = odeme.odeme_yontemi === 'SENET' ? 'Senet' : 'Çek';
      toast.success(
        durum === 'TAHSIL_EDILDI' ? `${tur} tahsil edildi olarak işaretlendi`
          : durum === 'KARSILIKSIZ' ? `${tur} karşılıksız işaretlendi`
            : 'Durum güncellendi'
      );
    } catch (e) {
      setOdemeler(prev => prev.map(o => o.id === odeme.id ? { ...o, cek_durum: onceki } : o));
      logError(e);
      toast.error('İşaretleme başarısız');
    } finally {
      setPatchInFlight(prev => { const n = new Set(prev); n.delete(odeme.id); return n; });
    }
  };

  // Yöntem koduna göre toplam (API'den beslenen kartlar).
  const toplamlar = useMemo(() => yontemBazliToplam(odemeler), [odemeler]);
  const toplamGenel = useMemo(() => odemeler.reduce((s, o) => s + (o.tutar || 0), 0), [odemeler]);

  // Vade takvimi kovaları (saf util).
  const bugun = new Date().toISOString().split('T')[0];
  const kovalar = useMemo(() => gruplaVadeKovalari(odemeler, bugun), [odemeler, bugun]);
  const vadePaneliBos = KOVA_TANIM.every(k => (kovalar[k.kod] || []).length === 0);

  // Yöntem kodu → görünen ad (API).
  const yontemAd = useCallback((kod) => yontemler.find(y => y.kod === kod)?.ad || kod, [yontemler]);

  // İstemci-tarafı arama (tarih/firma/yöntem backend'de uygulandı).
  const filtered = useMemo(() => {
    const q = normalizeForSearch(arama);
    if (!q) return odemeler;
    return odemeler.filter(o =>
      searchMatchPre(tedarikciler[o.tedarikci_id], q) ||
      searchMatchPre(o.cek_no, q) ||
      searchMatchPre(o.aciklama, q)
    );
  }, [odemeler, arama, tedarikciler]);

  // Tedarikçiye göre gruplama (toggle).
  const gruplar = useMemo(() => {
    if (gorunumNorm !== 'grup') return [];
    const map = {};
    filtered.forEach(o => {
      const key = o.tedarikci_id ?? '_yok';
      if (!map[key]) map[key] = { tedarikci_id: o.tedarikci_id, ad: tedarikciler[o.tedarikci_id] || `#${o.tedarikci_id}`, rows: [], toplam: 0 };
      map[key].rows.push(o);
      map[key].toplam += (o.tutar || 0);
    });
    return Object.values(map);
  }, [filtered, gorunumNorm, tedarikciler]);

  const setBuAy = () => {
    const d = new Date();
    const ilk = new Date(d.getFullYear(), d.getMonth(), 1);
    const son = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    setBaslangic(ilk.toISOString().split('T')[0]);
    setBitis(son.toISOString().split('T')[0]);
  };
  const setGecenAy = () => {
    const d = new Date();
    const ilk = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const son = new Date(d.getFullYear(), d.getMonth(), 0);
    setBaslangic(ilk.toISOString().split('T')[0]);
    setBitis(son.toISOString().split('T')[0]);
  };
  const setSon3Ay = () => {
    const d = new Date();
    const ilk = new Date(d.getFullYear(), d.getMonth() - 2, 1);
    const son = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    setBaslangic(ilk.toISOString().split('T')[0]);
    setBitis(son.toISOString().split('T')[0]);
  };

  const handleCsv = () => {
    exportCsv(
      odemelerToCsvRows(filtered, tedarikciler),
      `odeme-takibi-${baslangic || 'tum'}-${bitis || 'tum'}.csv`
    );
  };

  const firmaOptions = useMemo(
    () => [{ value: '', label: 'Tüm Firmalar' }, ...firmalar.filter(f => f.aktif !== false).map(f => ({ value: f.kod, label: f.ad }))],
    [firmalar]
  );
  const yontemOptions = useMemo(
    () => [{ value: '', label: 'Tüm Yöntemler' }, ...yontemler.map(y => ({ value: y.kod, label: y.ad }))],
    [yontemler]
  );

  const presetBtnStyle = {
    padding: '7px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    background: colors.surface, cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: colors.textSecondary,
  };

  // Tablo satırı (düz + grup ortak).
  const renderRow = (o) => {
    const cekRow = CEK_YONTEMLERI.includes(o.odeme_yontemi);
    const bekliyor = cekRow && (o.cek_durum || 'BEKLEMEDE') === 'BEKLEMEDE';
    return (
      <tr key={o.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
        <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 500 }}>{o.odeme_tarihi || '-'}</td>
        <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
          {tedarikciler[o.tedarikci_id] || `#${o.tedarikci_id}`}
        </td>
        <td style={{ padding: '12px 16px' }}>
          <YontemBadge yontem={o.odeme_yontemi} label={yontemAd(o.odeme_yontemi)} />
        </td>
        <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', fontWeight: 700, color: colors.success, fontVariantNumeric: 'tabular-nums' }}>
          {fmt(o.tutar)} <span style={{ fontSize: '11px', fontWeight: 500, color: colors.textTertiary }}>TL</span>
        </td>
        <td style={{ padding: '12px 16px', fontSize: '12px', color: o.cek_no ? colors.textPrimary : colors.textTertiary }}>
          {o.cek_no || '-'}
        </td>
        <td style={{ padding: '12px 16px', fontSize: '12px', color: o.cek_vade_tarihi ? colors.textPrimary : colors.textTertiary, fontWeight: o.cek_vade_tarihi ? 500 : 400 }}>
          {o.cek_vade_tarihi || '-'}
        </td>
        <td style={{ padding: '12px 16px' }}>
          {cekRow ? <DurumBadge durum={o.cek_durum || 'BEKLEMEDE'} /> : <span style={{ color: colors.textTertiary }}>—</span>}
        </td>
        <td style={{ padding: '12px 16px', fontSize: '12px', color: colors.textSecondary, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {o.aciklama || '-'}
        </td>
        {canManage && (
          <td style={{ padding: '12px 16px' }}>
            {bekliyor ? <AksiyonButonlari odeme={o} onIsaretle={isaretle} compact busy={patchInFlight.has(o.id)} /> : null}
          </td>
        )}
      </tr>
    );
  };

  const sutunSayisi = canManage ? 9 : 8;

  if (loading && odemeler.length === 0) {
    return (
      <div style={{ padding: '20px' }}>
        <h2 style={{ margin: '0 0 20px', color: colors.textPrimary, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em' }}>Ödeme Takibi</h2>
        <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  {Array.from({ length: 8 }).map((__, j) => (
                    <td key={j} style={{ padding: '12px 16px' }}>
                      <div style={{ height: '12px', borderRadius: '4px', background: colors.border, width: j === 1 ? '80%' : '60%' }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ margin: '0 0 20px', color: colors.textPrimary, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em' }}>
        Ödeme Takibi
      </h2>

      {/* KPI kartları — API'den beslenen yöntem listesi */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        <div style={{
          background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${colors.primary}`,
          padding: '16px', boxShadow: shadows.sm,
        }}>
          <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
            Toplam Ödeme
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
            {fmt(toplamGenel)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
          </div>
        </div>
        {yontemler.map(y => {
          const renk = YONTEM_RENK[y.kod] || YONTEM_RENK_FALLBACK;
          const aktif = yontem === y.kod;
          return (
            <div
              key={y.kod}
              onClick={() => setYontem(aktif ? '' : y.kod)}
              style={{
                background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${renk.color}`,
                padding: '16px', cursor: 'pointer',
                boxShadow: aktif ? shadows.md : shadows.sm,
                outline: aktif ? `2px solid ${renk.color}` : 'none',
                transition: 'box-shadow 0.15s',
              }}>
              <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                {y.ad}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
                {fmt(toplamlar[y.kod] || 0)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Vade Takvimi — bekleyen çek/senetlerin radar paneli */}
      {!vadePaneliBos && (
        <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, padding: '16px 20px', marginBottom: '24px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: colors.textPrimary, marginBottom: '14px' }}>
            🗓️ Vade Takvimi <span style={{ fontSize: '12px', fontWeight: 400, color: colors.textTertiary }}>(bekleyen çek/senet)</span>
          </div>
          {KOVA_TANIM.map(kova => {
            const liste = kovalar[kova.kod] || [];
            if (liste.length === 0) return null;
            const toplam = liste.reduce((s, o) => s + (o.tutar || 0), 0);
            return (
              <div key={kova.kod} style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: kova.renk, display: 'inline-block' }} />
                  <span style={{ fontSize: '13px', fontWeight: 700, color: kova.renk }}>{kova.baslik}</span>
                  <span style={{ fontSize: '12px', color: colors.textTertiary }}>
                    {liste.length} kayıt · {fmt(toplam)} TL
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {liste.map(o => (
                    <div key={o.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                      padding: '8px 12px', background: colors.backgroundAlt, borderRadius: radius.sm,
                      borderLeft: `3px solid ${kova.renk}`, flexWrap: 'wrap',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                          {tedarikciler[o.tedarikci_id] || `#${o.tedarikci_id}`}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(o.tutar)} TL
                        </span>
                        <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                          Vade: {o.cek_vade_tarihi || '-'}
                        </span>
                      </div>
                      {canManage && <AksiyonButonlari odeme={o} onIsaretle={isaretle} busy={patchInFlight.has(o.id)} />}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtre çubuğu */}
      <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, padding: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 600 }}>Hızlı Aralık</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={setBuAy} style={presetBtnStyle}>Bu Ay</button>
              <button onClick={setGecenAy} style={presetBtnStyle}>Geçen Ay</button>
              <button onClick={setSon3Ay} style={presetBtnStyle}>Son 3 Ay</button>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 600 }}>Başlangıç</label>
            <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)}
              style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 600 }}>Bitiş</label>
            <input type="date" value={bitis} onChange={e => setBitis(e.target.value)}
              style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }} />
          </div>
          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 600 }}>Firma</label>
            <SearchableSelect options={firmaOptions} value={firma} onChange={setFirma} placeholder="Tüm Firmalar" compact />
          </div>
          <div style={{ minWidth: '180px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 600 }}>Yöntem</label>
            <SearchableSelect options={yontemOptions} value={yontem} onChange={setYontem} placeholder="Tüm Yöntemler" compact />
          </div>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 600 }}>Ara</label>
            <input value={arama} onChange={e => setArama(e.target.value)}
              placeholder="Tedarikçi, çek no veya açıklama…"
              style={{ width: '100%', padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginTop: '14px' }}>
          <div style={{ display: 'inline-flex', border: `1px solid ${colors.border}`, borderRadius: radius.sm, overflow: 'hidden' }}>
            <button
              onClick={() => setGorunum('duz')}
              style={{
                padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                background: gorunumNorm === 'duz' ? colors.primary : colors.surface,
                color: gorunumNorm === 'duz' ? '#fff' : colors.textSecondary,
              }}>
              Düz Liste
            </button>
            <button
              onClick={() => setGorunum('grup')}
              style={{
                padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                background: gorunumNorm === 'grup' ? colors.primary : colors.surface,
                color: gorunumNorm === 'grup' ? '#fff' : colors.textSecondary,
              }}>
              Tedarikçiye Göre
            </button>
          </div>
          <button
            onClick={handleCsv}
            style={{
              padding: '7px 16px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
              background: colors.surface, cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: colors.textSecondary,
            }}>
            CSV İndir
          </button>
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: colors.textSecondary }}>
            {filtered.length} / {odemeler.length} kayıt
          </span>
        </div>
      </div>

      {/* Tablo */}
      <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: colors.backgroundAlt }}>
              <th style={thStyle()}>İşlem Tarihi</th>
              <th style={thStyle()}>Tedarikçi</th>
              <th style={thStyle()}>Yöntem</th>
              <th style={thStyle('right')}>Tutar</th>
              <th style={thStyle()}>Çek No</th>
              <th style={thStyle()}>Çek Vade</th>
              <th style={thStyle()}>Durum</th>
              <th style={thStyle()}>Açıklama</th>
              {canManage && <th style={thStyle()}>Aksiyon</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={sutunSayisi} style={{ padding: '32px 16px', textAlign: 'center', color: colors.textTertiary, fontSize: '13px' }}>
                  Ödeme kaydı bulunamadı
                </td>
              </tr>
            ) : gorunumNorm === 'grup' ? (
              gruplar.map(g => (
                <React.Fragment key={g.tedarikci_id ?? '_yok'}>
                  <tr style={{ background: colors.backgroundAlt }}>
                    <td colSpan={3} style={{ padding: '10px 16px', fontSize: '13px', fontWeight: 700, color: colors.textPrimary }}>
                      {g.ad} <span style={{ fontSize: '11px', fontWeight: 400, color: colors.textTertiary }}>({g.rows.length} kayıt)</span>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(g.toplam)} <span style={{ fontSize: '11px', fontWeight: 500, color: colors.textTertiary }}>TL</span>
                    </td>
                    <td colSpan={sutunSayisi - 4} />
                  </tr>
                  {g.rows.map(renderRow)}
                </React.Fragment>
              ))
            ) : (
              filtered.map(renderRow)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OdemeTakibiPage;
